import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { AuthContext } from "../App";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, X, Save, Loader2, Printer, FileText, ShoppingCart, Receipt,
  AlertTriangle, CloudRain, Wrench, DollarSign, Users, Calendar, CheckCircle2, RefreshCw, Copy, Layers,
} from "lucide-react";
import FacturaFormModal from "../components/FacturaFormModal";
import EgresosPage from "./EgresosPage";
import { EntidadModal } from "./CatastroPage";
import { buildReporteConstructora, imprimirReporte, fmtMon } from "../lib/constructoraReportes";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BTN_PRI = "flex items-center gap-2 px-4 py-2 text-sm bg-arandu-blue hover:bg-blue-600 text-white rounded-lg transition-all font-body disabled:opacity-50";
const BTN_SEC = "flex items-center gap-2 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg transition-all border border-white/10 disabled:opacity-50";
const INPUT = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-arandu-blue/60";
const LABEL = "block text-xs text-slate-400 mb-1";
const CARD = "bg-white/5 border border-white/10 rounded-xl";

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const hoy = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const errTxt = (e, fb = "Error") => {
  if (!e) return fb;
  if (typeof e.detail === "string") return e.detail;
  if (e.detail?.mensaje) return e.detail.mensaje;
  if (Array.isArray(e.detail)) return e.detail.map((d) => d.msg).join(" · ");
  return fb;
};

// fetch JSON con manejo de errores uniforme
function useApi() {
  const { token } = useContext(AuthContext);
  return useCallback(async (path, { method = "GET", body } = {}) => {
    const res = await fetch(`${API}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, ...(body ? { "Content-Type": "application/json" } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { const err = new Error(errTxt(data, "Error")); err.status = res.status; err.data = data; throw err; }
    return data;
  }, [token]);
}

const Modal = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
    <div className={`bg-arandu-dark border border-white/10 rounded-2xl w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto p-6`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white">{title}</h2>
        <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

const useCan = () => {
  const { hasPermission, user } = useContext(AuthContext);
  const isAdmin = ["admin", "super_admin", "gerente"].includes(user?.role);
  return useCallback((p) => isAdmin || hasPermission(p), [isAdmin, hasPermission]);
};

// ═════════════════════════════════════════════════════════════════════════════
// PRESUPUESTO / PLANILLA DE CÓMPUTO MÉTRICO
// ═════════════════════════════════════════════════════════════════════════════

// ── Inputs numéricos con separador de miles (1.234.567,5) ───────────────────
const UNIDADES = ["un", "m2", "m3", "ml", "m", "kg", "tn", "lt", "gl", "hs", "día", "mes", "pza", "bolsa", "jornal", "global", "juego", "rollo", "varilla", "plancha"];
const fmtMiles = (v, dec = 4) => {
  if (v === "" || v == null || isNaN(Number(v))) return "";
  const [i, d] = Number(v).toFixed(dec).split(".");
  const dd = (d || "").replace(/0+$/, "");
  return i.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + (dd ? "," + dd : "");
};

function NumInput({ value, onChange, disabled, className = "", decimals = true, placeholder }) {
  const [txt, setTxt] = useState(fmtMiles(value));
  const [foco, setFoco] = useState(false);
  useEffect(() => { if (!foco) setTxt(fmtMiles(value)); }, [value, foco]);
  const cambiar = (e) => {
    const raw = e.target.value;
    const caret = e.target.selectionStart ?? raw.length;
    const limpioAntes = raw.slice(0, caret).replace(/[^\d,]/g, "").length;
    let limpio = raw.replace(/[^\d,]/g, "");
    if (!decimals) limpio = limpio.replace(/,/g, "");
    const k = limpio.indexOf(",");
    const ent = (k >= 0 ? limpio.slice(0, k) : limpio).replace(/^0+(?=\d)/, "");
    const dec = k >= 0 ? limpio.slice(k + 1).replace(/,/g, "").slice(0, 4) : null;
    const nuevo = ent.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + (dec !== null ? "," + dec : "");
    setTxt(nuevo);
    onChange(raw === "" ? "" : (ent || "0") + (dec ? "." + dec : ""));
    // reubica el cursor según los dígitos que había antes de él
    const el = e.target;
    requestAnimationFrame(() => {
      let c = 0, pos = 0;
      while (pos < nuevo.length && c < limpioAntes) { if (/[\d,]/.test(nuevo[pos])) c++; pos++; }
      try { el.setSelectionRange(pos, pos); } catch { /* ignore */ }
    });
  };
  return (
    <input disabled={disabled} inputMode="decimal" placeholder={placeholder} className={className}
      value={txt} onFocus={() => setFoco(true)} onBlur={() => { setFoco(false); setTxt(fmtMiles(value)); }} onChange={cambiar} />
  );
}

const ESTADO_PRES = {
  borrador: ["Borrador", "text-slate-300 bg-slate-400/10"],
  enviado: ["Enviado", "text-blue-300 bg-blue-400/10"],
  aprobado: ["Aprobado", "text-emerald-300 bg-emerald-400/10"],
  rechazado: ["Rechazado", "text-rose-300 bg-rose-400/10"],
  reemplazado: ["Reemplazado", "text-amber-300 bg-amber-400/10"],
};

const puVenta = (it) => {
  const subs = it.subitems || [];
  if (subs.length) return subs.reduce((a, s) => a + (Number(s.cantidad) || 0) * (Number(s.costo_unitario) || 0) * (1 + (Number(s.ganancia_pct) || 0) / 100), 0);
  return Number(it.precio_unitario) || 0;
};
const puCosto = (it) => (it.subitems || []).reduce((a, s) => a + (Number(s.cantidad) || 0) * (Number(s.costo_unitario) || 0), 0);

// Impresión para el cliente: SIN sub-ítems, costos ni ganancias
function imprimirPresupuesto(pres, proyecto, m) {
  const esc = (t) => String(t ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  let sin = 0;
  const filas = pres.rubros.map((r) => {
    let sub = 0;
    const its = r.items.map((it) => {
      const st = (Number(it.cantidad) || 0) * puVenta(it);
      sub += st;
      return `<tr><td>${esc(it.codigo)}</td><td>${esc(it.descripcion)}</td><td class="r">${fmtMiles(it.cantidad)}</td><td>${esc(it.unidad)}</td><td class="r">${fmtMon(puVenta(it), m)}</td><td class="r">${fmtMon(st, m)}</td></tr>`;
    }).join("");
    sin += sub;
    return `<tr class="rub"><td>${esc(r.codigo)}</td><td colspan="4">${esc(r.nombre)}</td><td class="r">${fmtMon(sub, m)}</td></tr>${its}`;
  }).join("");
  const iva = sin * (Number(pres.iva_pct) || 0) / 100;
  const cond = pres.condicion_pago === "credito"
    ? `Crédito${pres.cuotas ? ` en ${pres.cuotas} cuotas` : ""}${pres.anticipo_pct ? ` — anticipo ${pres.anticipo_pct}%` : ""}` : "Contado";
  const w = window.open("", "_blank");
  if (!w) return toast.error("El navegador bloqueó la ventana de impresión");
  w.document.write(`<html><head><title>${esc(pres.nombre)}</title><style>
    body{font-family:Arial,sans-serif;font-size:12px;color:#111;margin:24px}h1{font-size:18px;margin:0 0 4px}
    .meta{margin:8px 0 14px;line-height:1.6}table{width:100%;border-collapse:collapse}
    th,td{border:1px solid #bbb;padding:4px 6px;text-align:left}th{background:#eee}.r{text-align:right}
    .rub td{background:#f3f3f3;font-weight:bold}.tot{margin-left:auto;width:300px;margin-top:10px}.tot div{display:flex;justify-content:space-between;padding:2px 0}
  </style></head><body>
    <h1>PRESUPUESTO — ${esc(pres.nombre)}</h1>
    <div class="meta"><b>Proyecto:</b> ${esc(proyecto.numero)} ${esc(proyecto.nombre)}<br><b>Cliente:</b> ${esc(proyecto.cliente_nombre)}<br>
      <b>Fecha:</b> ${esc(pres.fecha_emision)} &nbsp; <b>Validez:</b> ${pres.validez_dias || "—"} días${pres.fecha_validez ? ` (hasta ${esc(pres.fecha_validez)})` : ""}<br>
      <b>Condición de pago:</b> ${esc(cond)}${pres.condicion_notas ? ` — ${esc(pres.condicion_notas)}` : ""}</div>
    <table><thead><tr><th>Ítem</th><th>Descripción</th><th class="r">Cantidad</th><th>Unidad</th><th class="r">P. unitario</th><th class="r">Subtotal</th></tr></thead><tbody>${filas}</tbody></table>
    <div class="tot"><div><span>Total sin IVA</span><b>${fmtMon(sin, m)}</b></div><div><span>IVA (${pres.iva_pct}%)</span><b>${fmtMon(iva, m)}</b></div><div><span><b>TOTAL</b></span><b>${fmtMon(sin + iva, m)}</b></div></div>
    ${pres.notas ? `<p style="margin-top:14px"><b>Observaciones:</b> ${esc(pres.notas)}</p>` : ""}
    <script>window.onload=()=>window.print()</script></body></html>`);
  w.document.close();
}

export function PresupuestoTab({ proyecto }) {
  const api = useApi();
  const can = useCan();
  const canEdit = can("constructora_presupuesto.editar");
  const canAprobar = can("constructora_presupuesto.aprobar");
  const canCopiar = can("constructora_presupuesto.copiar");
  const [lista, setLista] = useState(null);
  const [abierto, setAbierto] = useState(null);   // id del presupuesto en edición
  const [copiarModal, setCopiarModal] = useState(null);
  const base = `/admin/constructora/proyectos/${proyecto.id}/presupuestos`;
  const m = proyecto.moneda || "PYG";

  const cargar = useCallback(async () => {
    try { setLista(await api(base)); } catch (e) { toast.error(e.message); }
  }, [api, base]);
  useEffect(() => { cargar(); }, [cargar]);

  const nuevo = async () => {
    const nombre = window.prompt("Nombre del presupuesto", `Presupuesto ${(lista?.length || 0) + 1}`);
    if (nombre === null) return;
    try { const d = await api(base, { method: "POST", body: { nombre } }); await cargar(); setAbierto(d.id); } catch (e) { toast.error(e.message); }
  };
  const duplicar = async (pr) => {
    const nombre = window.prompt("Nombre de la copia", `${pr.nombre} (copia)`);
    if (nombre === null) return;
    try { await api(`${base}/${pr.id}/copiar`, { method: "POST", body: { nombre } }); toast.success("Presupuesto copiado"); cargar(); } catch (e) { toast.error(e.message); }
  };
  const estado = async (pr, est) => {
    let motivo = null;
    if (est === "rechazado") { motivo = window.prompt("Motivo del rechazo"); if (!motivo) return; }
    if (est === "aprobado" && !window.confirm(`¿Aprobar «${pr.nombre}»? Pasa a regir el proyecto (facturación, cronograma, reportes) y el presupuesto aprobado anterior queda como «reemplazado».`)) return;
    try { await api(`${base}/${pr.id}/estado`, { method: "POST", body: { estado: est, motivo } }); toast.success("Estado actualizado"); cargar(); } catch (e) { toast.error(e.message); }
  };
  const eliminar = async (pr) => {
    if (!window.confirm(`¿Eliminar «${pr.nombre}»?`)) return;
    try { await api(`${base}/${pr.id}`, { method: "DELETE" }); cargar(); } catch (e) { toast.error(e.message); }
  };
  const imprimir = async (pr) => {
    try { imprimirPresupuesto(await api(`${base}/${pr.id}`), proyecto, m); } catch (e) { toast.error(e.message); }
  };

  if (abierto) return <PresupuestoEditor proyecto={proyecto} prid={abierto} onVolver={() => { setAbierto(null); cargar(); }} />;
  if (!lista) return <Loader2 className="w-6 h-6 animate-spin text-arandu-blue" />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {canEdit && <button className={BTN_PRI} onClick={nuevo}><Plus className="w-4 h-4" /> Nuevo presupuesto</button>}
        {canEdit && canCopiar && <button className={BTN_SEC} onClick={() => setCopiarModal(true)}><Copy className="w-4 h-4" /> Copiar de otro proyecto</button>}
      </div>
      {lista.length === 0 && <div className="text-center text-slate-500 py-10 border border-dashed border-white/10 rounded-xl">Este proyecto no tiene presupuestos. Creá uno nuevo o copiá uno de otro proyecto.</div>}
      <div className="grid gap-3 lg:grid-cols-2">
        {lista.map((pr) => {
          const [lbl, cls] = ESTADO_PRES[pr.estado] || ESTADO_PRES.borrador;
          return (
            <div key={pr.id} className={`${CARD} p-4 space-y-2 ${pr.estado === "aprobado" ? "border-emerald-500/40" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-white font-semibold truncate">{pr.nombre}</p>
                  <p className="text-xs text-slate-400">
                    {pr.condicion_pago === "credito" ? `Crédito${pr.cuotas ? ` · ${pr.cuotas} cuotas` : ""}${pr.anticipo_pct ? ` · anticipo ${pr.anticipo_pct}%` : ""}` : "Contado"}
                    {" · "}{pr.rubros_count} rubros
                  </p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${cls}`}>{lbl}</span>
              </div>
              <div className="flex justify-between items-end">
                <div className="text-xs text-slate-400">
                  <p>Emitido: {pr.fecha_emision || "—"}</p>
                  <p className={pr.vencido ? "text-rose-400" : ""}>Válido hasta: {pr.fecha_validez || "—"}{pr.vencido ? " (vencido)" : ""}</p>
                  {pr.motivo_estado && <p className="text-slate-500">{pr.motivo_estado}</p>}
                </div>
                <p className="text-lg font-bold text-white">{fmtMon(pr.total_con_iva, m)}</p>
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1 border-t border-white/5">
                <button className={BTN_SEC} onClick={() => setAbierto(pr.id)}><Pencil className="w-3.5 h-3.5" /> {canEdit ? "Editar" : "Ver"}</button>
                <button className={BTN_SEC} onClick={() => imprimir(pr)}><Printer className="w-3.5 h-3.5" /> Imprimir</button>
                {canEdit && canCopiar && <button className={BTN_SEC} onClick={() => duplicar(pr)}><Copy className="w-3.5 h-3.5" /> Copiar</button>}
                {canAprobar && pr.estado !== "aprobado" && <button className={`${BTN_SEC} !text-emerald-300`} onClick={() => estado(pr, "aprobado")}><CheckCircle2 className="w-3.5 h-3.5" /> Aprobar</button>}
                {canAprobar && ["borrador"].includes(pr.estado) && <button className={BTN_SEC} onClick={() => estado(pr, "enviado")}>Marcar enviado</button>}
                {canAprobar && ["borrador", "enviado"].includes(pr.estado) && <button className={`${BTN_SEC} !text-rose-300`} onClick={() => estado(pr, "rechazado")}><X className="w-3.5 h-3.5" /> Rechazar</button>}
                {canAprobar && ["rechazado", "reemplazado"].includes(pr.estado) && <button className={BTN_SEC} onClick={() => estado(pr, "borrador")}>Reabrir</button>}
                {canEdit && pr.estado !== "aprobado" && <button className="ml-auto text-rose-400 p-2" onClick={() => eliminar(pr)}><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          );
        })}
      </div>
      {copiarModal && <CopiarDeOtroModal proyecto={proyecto} onClose={() => setCopiarModal(null)} onDone={() => { setCopiarModal(null); cargar(); }} />}
    </div>
  );
}

function CopiarDeOtroModal({ proyecto, onClose, onDone }) {
  const api = useApi();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api(`/admin/constructora/presupuestos-disponibles?proyecto_id=${proyecto.id}`).then(setItems).catch((e) => { toast.error(e.message); setItems([]); }); }, [api, proyecto.id]);
  const copiar = async (it) => {
    try {
      await api(`/admin/constructora/proyectos/${proyecto.id}/presupuestos`, { method: "POST", body: { nombre: `${it.nombre} (copia)`, copiar_de_proyecto_id: it.proyecto_id, copiar_de_presupuesto_id: it.presupuesto_id } });
      toast.success("Presupuesto copiado a este proyecto (avances en 0)"); onDone();
    } catch (e) { toast.error(e.message); }
  };
  const vis = (items || []).filter((i) => `${i.proyecto} ${i.nombre}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Modal title="Copiar presupuesto de otro proyecto" onClose={onClose} wide>
      <input className={`${INPUT} mb-3`} placeholder="Buscar proyecto o presupuesto…" value={q} onChange={(e) => setQ(e.target.value)} />
      {!items ? <Loader2 className="w-5 h-5 animate-spin text-arandu-blue" /> : vis.length === 0 ? <p className="text-slate-500 text-sm">No hay presupuestos en otros proyectos.</p> : (
        <div className="space-y-1.5">
          {vis.map((it) => (
            <div key={it.presupuesto_id} className="flex items-center justify-between gap-3 bg-white/5 rounded-lg px-3 py-2">
              <div className="min-w-0"><p className="text-sm text-white truncate">{it.nombre}</p><p className="text-xs text-slate-400 truncate">{it.proyecto} · {(ESTADO_PRES[it.estado] || [it.estado])[0]}</p></div>
              <span className="text-sm text-slate-300 whitespace-nowrap">{fmtMon(it.total_con_iva, proyecto.moneda || "PYG")}</span>
              <button className={BTN_SEC} onClick={() => copiar(it)}><Copy className="w-3.5 h-3.5" /> Copiar</button>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function PresupuestoEditor({ proyecto, prid, onVolver }) {
  const api = useApi();
  const can = useCan();
  const canEdit = can("constructora_presupuesto.editar");
  const canCostos = can("constructora_presupuesto.ver_costos");
  const [pres, setPres] = useState(null);
  const [saving, setSaving] = useState(false);
  const [abiertos, setAbiertos] = useState({});   // composición abierta por ítem
  const [unidades, setUnidades] = useState(UNIDADES);  // configurables por empresa (Configuración → Unidades)
  const m = proyecto.moneda || "PYG";
  const url = `/admin/constructora/proyectos/${proyecto.id}/presupuestos/${prid}`;

  const cargar = useCallback(async () => {
    try { setPres(await api(url)); } catch (e) { toast.error(e.message); }
  }, [api, url]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    let vivo = true;
    api(`/admin/constructora/unidades?solo_activos=true${proyecto.logo_tipo ? `&logo_tipo=${encodeURIComponent(proyecto.logo_tipo)}` : ""}`)
      .then((l) => { if (vivo && Array.isArray(l) && l.length) setUnidades(l.map((u) => u.nombre)); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [api, proyecto.logo_tipo]);

  const upd = (fn) => setPres((p) => { const c = JSON.parse(JSON.stringify(p)); fn(c); return c; });
  const totales = useMemo(() => {
    if (!pres) return { sin: 0, iva: 0, con: 0, avance: 0, costo: 0 };
    let sin = 0, av = 0, costo = 0;
    pres.rubros.forEach((r) => {
      const sub = r.items.reduce((a, it) => a + (Number(it.cantidad) || 0) * puVenta(it), 0);
      costo += r.items.reduce((a, it) => a + (Number(it.cantidad) || 0) * puCosto(it), 0);
      sin += sub; av += sub * (Number(r.avance_pct) || 0) / 100;
    });
    const iva = sin * (Number(pres.iva_pct) || 0) / 100;
    return { sin, iva, con: sin + iva, avance: sin ? av / sin * 100 : 0, costo };
  }, [pres]);

  const guardar = async () => {
    setSaving(true);
    try {
      const body = {
        nombre: pres.nombre, iva_pct: Number(pres.iva_pct) || 0, concepto_factura: pres.concepto_factura, notas: pres.notas,
        condicion_pago: pres.condicion_pago || "contado", cuotas: pres.cuotas ? Number(pres.cuotas) : null,
        anticipo_pct: pres.anticipo_pct ? Number(pres.anticipo_pct) : null, condicion_notas: pres.condicion_notas,
        fecha_emision: pres.fecha_emision, validez_dias: pres.validez_dias ? Number(pres.validez_dias) : null,
        rubros: pres.rubros.map((r) => ({
          ...r, avance_pct: Number(r.avance_pct) || 0,
          items: r.items.map((it) => ({
            ...it, cantidad: Number(it.cantidad) || 0, precio_unitario: Number(it.precio_unitario) || 0,
            subitems: (it.subitems || []).map((s) => ({ ...s, cantidad: Number(s.cantidad) || 0, costo_unitario: Number(s.costo_unitario) || 0, ganancia_pct: Number(s.ganancia_pct) || 0 })),
          })),
        })),
      };
      setPres(await api(url, { method: "PUT", body }));
      toast.success("Presupuesto guardado");
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  if (!pres) return <Loader2 className="w-6 h-6 animate-spin text-arandu-blue" />;
  const [lbl, cls] = ESTADO_PRES[pres.estado] || ESTADO_PRES.borrador;
  const nuevoSub = () => ({ descripcion: "", cantidad: 1, unidad: "un", costo_unitario: 0, ganancia_pct: 0 });

  return (
    <div className="space-y-4">
      <datalist id="unidades-obra">{unidades.map((u) => <option key={u} value={u} />)}</datalist>
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <button className={BTN_SEC} onClick={onVolver}>← Presupuestos</button>
          <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{lbl}</span>
          {canEdit && <button className={BTN_SEC} onClick={() => upd((c) => c.rubros.push({ codigo: String(c.rubros.length + 1), nombre: "", avance_pct: 0, items: [] }))}><Plus className="w-4 h-4" /> Rubro</button>}
          <button className={BTN_SEC} onClick={() => imprimirPresupuesto(pres, proyecto, m)}><Printer className="w-4 h-4" /> Imprimir</button>
        </div>
        {canEdit && <button className={BTN_PRI} onClick={guardar} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar</button>}
      </div>

      <div className={`${CARD} p-3 grid gap-3 md:grid-cols-4`}>
        <div className="md:col-span-2"><label className={LABEL}>Nombre del presupuesto</label>
          <input disabled={!canEdit} className={INPUT} value={pres.nombre || ""} onChange={(e) => upd((c) => { c.nombre = e.target.value; })} /></div>
        <div><label className={LABEL}>Fecha de emisión</label>
          <DateInput disabled={!canEdit} className={INPUT} value={(pres.fecha_emision || "").slice(0, 10)} onChange={(e) => upd((c) => { c.fecha_emision = e.target.value; })} /></div>
        <div><label className={LABEL}>Validez (días){pres.fecha_validez ? ` — hasta ${pres.fecha_validez}` : ""}</label>
          <NumInput decimals={false} disabled={!canEdit} className={`${INPUT} text-right`} value={pres.validez_dias ?? ""} onChange={(v) => upd((c) => { c.validez_dias = v; })} /></div>
        <div><label className={LABEL}>Condición de pago</label>
          <select disabled={!canEdit} className={INPUT} value={pres.condicion_pago || "contado"} onChange={(e) => upd((c) => { c.condicion_pago = e.target.value; })}>
            <option value="contado">Contado</option><option value="credito">Crédito</option>
          </select></div>
        {pres.condicion_pago === "credito" && (<>
          <div><label className={LABEL}>Cantidad de cuotas</label>
            <NumInput decimals={false} disabled={!canEdit} className={`${INPUT} text-right`} value={pres.cuotas ?? ""} onChange={(v) => upd((c) => { c.cuotas = v; })} /></div>
          <div><label className={LABEL}>Anticipo (%)</label>
            <NumInput disabled={!canEdit} className={`${INPUT} text-right`} value={pres.anticipo_pct ?? ""} onChange={(v) => upd((c) => { c.anticipo_pct = v; })} /></div>
        </>)}
        <div className={pres.condicion_pago === "credito" ? "md:col-span-4" : "md:col-span-3"}><label className={LABEL}>Detalle de la condición (opcional)</label>
          <input disabled={!canEdit} className={INPUT} value={pres.condicion_notas || ""} onChange={(e) => upd((c) => { c.condicion_notas = e.target.value; })} placeholder="Ej: cuotas mensuales, primera a los 30 días" /></div>
      </div>

      <div className={`${CARD} p-3 grid gap-3 md:grid-cols-[1fr,120px]`}>
        <div>
          <label className={LABEL}>Texto que sale en cada factura de este proyecto (usá {"{proyecto}"}, {"{mes}"}, {"{cliente}"}; se puede modificar al facturar)</label>
          <input disabled={!canEdit} className={INPUT} value={pres.concepto_factura || ""} onChange={(e) => upd((c) => { c.concepto_factura = e.target.value; })} />
        </div>
        <div>
          <label className={LABEL}>IVA %</label>
          <NumInput disabled={!canEdit} className={`${INPUT} text-right`} value={pres.iva_pct} onChange={(v) => upd((c) => { c.iva_pct = v; })} />
        </div>
      </div>

      {pres.rubros.length === 0 && <div className="text-center text-slate-500 py-10 border border-dashed border-white/10 rounded-xl">Sin rubros. Agregá el primer rubro con el botón «Rubro».</div>}

      {pres.rubros.map((r, ri) => {
        const sub = r.items.reduce((a, it) => a + (Number(it.cantidad) || 0) * puVenta(it), 0);
        return (
          <div key={r.id || ri} className={`${CARD} overflow-hidden`}>
            <div className="flex flex-wrap items-center gap-2 bg-white/5 px-3 py-2">
              <input disabled={!canEdit} className={`${INPUT} !w-16`} value={r.codigo || ""} onChange={(e) => upd((c) => { c.rubros[ri].codigo = e.target.value; })} />
              <input disabled={!canEdit} className={`${INPUT} flex-1 min-w-[200px] font-semibold`} value={r.nombre} placeholder="Nombre del rubro" onChange={(e) => upd((c) => { c.rubros[ri].nombre = e.target.value; })} />
              <label className="text-xs text-slate-400 flex items-center gap-1">Avance
                <NumInput disabled={!canEdit} className={`${INPUT} !w-16 text-right`} value={r.avance_pct ?? 0} onChange={(v) => upd((c) => { c.rubros[ri].avance_pct = v; })} />%
              </label>
              <span className="text-sm text-emerald-400 font-medium min-w-[120px] text-right">{fmtMon(sub, m)}</span>
              {canEdit && <button className="text-rose-400" onClick={() => upd((c) => { c.rubros.splice(ri, 1); })}><Trash2 className="w-4 h-4" /></button>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-slate-500 text-left"><th className="px-2 py-1 w-20">Ítem</th><th className="px-2 py-1">Descripción</th><th className="px-2 py-1 w-24 text-right">Cantidad</th><th className="px-2 py-1 w-24">Unidad</th><th className="px-2 py-1 w-32 text-right">P. unitario</th><th className="px-2 py-1 w-32 text-right">Subtotal</th><th className="w-14"></th></tr></thead>
                <tbody>
                  {r.items.map((it, ii) => {
                    const conSubs = (it.subitems?.length || 0) > 0 || it.con_composicion;
                    const key = `${ri}-${it.id || ii}`;
                    const abierta = !!abiertos[key];
                    return (
                      <React.Fragment key={it.id || ii}>
                        <tr className="border-t border-white/5">
                          <td className="px-2 py-1"><input disabled={!canEdit} className={INPUT} value={it.codigo || ""} onChange={(e) => upd((c) => { c.rubros[ri].items[ii].codigo = e.target.value; })} /></td>
                          <td className="px-2 py-1"><input disabled={!canEdit} className={INPUT} value={it.descripcion} onChange={(e) => upd((c) => { c.rubros[ri].items[ii].descripcion = e.target.value; })} /></td>
                          <td className="px-2 py-1"><NumInput disabled={!canEdit} className={`${INPUT} text-right`} value={it.cantidad} onChange={(v) => upd((c) => { c.rubros[ri].items[ii].cantidad = v; })} /></td>
                          <td className="px-2 py-1"><input disabled={!canEdit} list="unidades-obra" className={INPUT} value={it.unidad || ""} onChange={(e) => upd((c) => { c.rubros[ri].items[ii].unidad = e.target.value; })} /></td>
                          <td className="px-2 py-1">
                            <NumInput disabled={!canEdit || conSubs} className={`${INPUT} text-right ${conSubs ? "opacity-70" : ""}`}
                              value={conSubs && it.subitems?.length ? Math.round(puVenta(it) * 100) / 100 : it.precio_unitario}
                              onChange={(v) => upd((c) => { c.rubros[ri].items[ii].precio_unitario = v; })} />
                          </td>
                          <td className="px-2 py-1 text-right text-slate-300">{fmtMon((Number(it.cantidad) || 0) * (conSubs && it.subitems?.length ? puVenta(it) : Number(it.precio_unitario) || 0), m)}</td>
                          <td className="whitespace-nowrap">
                            {canCostos && <button title="Composición del precio (costo + % ganancia). No se imprime." className={`mr-1 ${conSubs ? "text-amber-300" : "text-slate-500"} hover:text-white`} onClick={() => setAbiertos((a) => ({ ...a, [key]: !a[key] }))}><Layers className="w-3.5 h-3.5" /></button>}
                            {canEdit && <button className="text-rose-400" onClick={() => upd((c) => { c.rubros[ri].items.splice(ii, 1); })}><Trash2 className="w-3.5 h-3.5" /></button>}
                          </td>
                        </tr>
                        {canCostos && abierta && (
                          <tr className="bg-amber-400/5">
                            <td></td>
                            <td colSpan={6} className="px-2 py-2">
                              <p className="text-[11px] text-amber-300 mb-1">Composición por unidad del ítem — uso interno (no se imprime). El precio unitario se calcula con costo + % de ganancia de cada sub-ítem.</p>
                              <table className="w-full text-xs">
                                <thead><tr className="text-slate-500 text-left"><th className="px-1 py-1">Sub-ítem</th><th className="px-1 w-20 text-right">Cant.</th><th className="px-1 w-20">Unid.</th><th className="px-1 w-28 text-right">Costo unit.</th><th className="px-1 w-20 text-right">% Gan.</th><th className="px-1 w-28 text-right">Precio venta</th><th className="w-6"></th></tr></thead>
                                <tbody>
                                  {(it.subitems || []).map((s, si) => (
                                    <tr key={s.id || si}>
                                      <td className="px-1 py-0.5"><input disabled={!canEdit} className={INPUT} value={s.descripcion} onChange={(e) => upd((c) => { c.rubros[ri].items[ii].subitems[si].descripcion = e.target.value; })} /></td>
                                      <td className="px-1"><NumInput disabled={!canEdit} className={`${INPUT} text-right`} value={s.cantidad} onChange={(v) => upd((c) => { c.rubros[ri].items[ii].subitems[si].cantidad = v; })} /></td>
                                      <td className="px-1"><input disabled={!canEdit} list="unidades-obra" className={INPUT} value={s.unidad || ""} onChange={(e) => upd((c) => { c.rubros[ri].items[ii].subitems[si].unidad = e.target.value; })} /></td>
                                      <td className="px-1"><NumInput disabled={!canEdit} className={`${INPUT} text-right`} value={s.costo_unitario} onChange={(v) => upd((c) => { c.rubros[ri].items[ii].subitems[si].costo_unitario = v; })} /></td>
                                      <td className="px-1"><NumInput disabled={!canEdit} className={`${INPUT} text-right`} value={s.ganancia_pct} onChange={(v) => upd((c) => { c.rubros[ri].items[ii].subitems[si].ganancia_pct = v; })} /></td>
                                      <td className="px-1 text-right text-slate-200">{fmtMon((Number(s.cantidad) || 0) * (Number(s.costo_unitario) || 0) * (1 + (Number(s.ganancia_pct) || 0) / 100), m)}</td>
                                      <td>{canEdit && <button className="text-rose-400" onClick={() => upd((c) => { c.rubros[ri].items[ii].subitems.splice(si, 1); })}><Trash2 className="w-3 h-3" /></button>}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              <div className="flex flex-wrap items-center justify-between gap-2 mt-1">
                                {canEdit && <button className="text-xs text-arandu-blue hover:underline flex items-center gap-1" onClick={() => upd((c) => { const x = c.rubros[ri].items[ii]; x.subitems = [...(x.subitems || []), nuevoSub()]; })}><Plus className="w-3 h-3" /> Sub-ítem</button>}
                                {(it.subitems?.length || 0) > 0 && (
                                  <p className="text-xs text-slate-300">Costo unit. {fmtMon(puCosto(it), m)} · Venta unit. <b className="text-white">{fmtMon(puVenta(it), m)}</b> · Ganancia {puCosto(it) ? ((puVenta(it) / puCosto(it) - 1) * 100).toFixed(1) : 0}%</p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {canEdit && <button className="m-2 text-xs text-arandu-blue hover:underline flex items-center gap-1" onClick={() => upd((c) => { c.rubros[ri].items.push({ codigo: `${r.codigo || ri + 1}.${String(r.items.length + 1).padStart(2, "0")}`, descripcion: "", cantidad: 1, unidad: "un", precio_unitario: 0, subitems: [] }); })}><Plus className="w-3 h-3" /> Ítem</button>}
          </div>
        );
      })}

      <div className={`${CARD} p-4 ml-auto max-w-sm space-y-1 text-sm`}>
        <div className="flex justify-between text-slate-300"><span>Total sin IVA</span><span>{fmtMon(totales.sin, m)}</span></div>
        <div className="flex justify-between text-slate-300"><span>IVA ({pres.iva_pct}%)</span><span>{fmtMon(totales.iva, m)}</span></div>
        <div className="flex justify-between text-white font-bold border-t border-white/10 pt-1"><span>Total con IVA</span><span>{fmtMon(totales.con, m)}</span></div>
        {canCostos && totales.costo > 0 && <div className="flex justify-between text-amber-300 text-xs"><span>Costo estimado (ítems con composición)</span><span>{fmtMon(totales.costo, m)}</span></div>}
        <div className="flex justify-between text-emerald-400"><span>Avance físico ponderado</span><span>{totales.avance.toFixed(1)}%</span></div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// CRONOGRAMA + CLIMA
// ═════════════════════════════════════════════════════════════════════════════

const ESTADO_T = {
  cumplida: ["Cumplida", "text-emerald-400 bg-emerald-400/10"],
  en_curso: ["En curso", "text-blue-400 bg-blue-400/10"],
  atrasada: ["Atrasada", "text-rose-400 bg-rose-400/10"],
  pendiente: ["Pendiente", "text-slate-400 bg-slate-400/10"],
};

export function CronogramaTab({ proyecto }) {
  const api = useApi();
  const can = useCan();
  const canEdit = can("constructora_cronograma.editar");
  const canClima = can("constructora_cronograma.clima");
  const [data, setData] = useState(null);
  const [tareas, setTareas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [clima, setClima] = useState({ fecha: hoy(), dias: 1, tipo: "lluvia", notas: "" });
  const [reporte, setReporte] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const d = await api(`/admin/constructora/proyectos/${proyecto.id}/cronograma`);
      setData(d); setTareas(d.tareas);
      if (can("constructora_reportes.ver")) setReporte(await api(`/admin/constructora/reportes/cronograma?proyecto_id=${proyecto.id}`));
    } catch (e) { toast.error(e.message); }
  }, [api, proyecto.id]); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar]);

  const upd = (i, patch) => setTareas((l) => l.map((t, j) => (j === i ? { ...t, ...patch } : t)));

  const guardar = async () => {
    const originales = Object.fromEntries((data?.tareas || []).map((t) => [t.id, t]));
    const cambioFechas = tareas.some((t) => originales[t.id] && (originales[t.id].inicio !== t.inicio || originales[t.id].fin !== t.fin));
    let motivo = null;
    if (cambioFechas) {
      motivo = window.prompt("Cambiaste fechas planificadas (reprogramación). ¿Motivo? (lluvia, falta de material, cliente…)");
      if (motivo === null) return;
    }
    setSaving(true);
    try {
      await api(`/admin/constructora/proyectos/${proyecto.id}/cronograma`, { method: "PUT", body: { tareas: tareas.map((t) => ({ ...t, avance_pct: Number(t.avance_pct) || 0 })), motivo } });
      toast.success("Cronograma guardado");
      cargar();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const desdePres = async () => {
    try {
      const r = await api(`/admin/constructora/proyectos/${proyecto.id}/cronograma/desde-presupuesto`, { method: "POST" });
      setTareas((l) => [...l, ...r.tareas]);
      toast.success("Rubros agregados como tareas. Completá las fechas y guardá.");
    } catch (e) { toast.error(e.message); }
  };

  // Gantt simple
  const rango = useMemo(() => {
    const f = tareas.flatMap((t) => [t.inicio, t.fin]).filter(Boolean).sort();
    if (!f.length) return null;
    const a = new Date(f[0]).getTime(), b = new Date(f[f.length - 1]).getTime();
    return { a, total: Math.max(b - a, 86400000) };
  }, [tareas]);
  const pos = (t) => {
    if (!rango || !t.inicio || !t.fin) return null;
    const l = (new Date(t.inicio).getTime() - rango.a) / rango.total * 100;
    const w = Math.max((new Date(t.fin).getTime() - new Date(t.inicio).getTime()) / rango.total * 100, 1.5);
    return { left: `${l}%`, width: `${w}%` };
  };

  const r = reporte?.resumen;
  return (
    <div className="space-y-4">
      {r && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {[["Avance promedio", `${r.avance_promedio_pct}%`, "text-white"], ["Tareas atrasadas", r.atrasadas, r.atrasadas ? "text-rose-400" : "text-emerald-400"],
            ["Corrimiento vs línea base", `${r.corrimiento_total_dias} d`, r.corrimiento_total_dias > 0 ? "text-amber-400" : "text-emerald-400"],
            ["Días de lluvia", r.dias_lluvia, "text-sky-400"], ["Atraso sin lluvia", `${r.atraso_sin_lluvia_dias} d`, r.atraso_sin_lluvia_dias > 0 ? "text-rose-400" : "text-emerald-400"]].map(([l, v, c]) => (
            <div key={l} className={`${CARD} p-3`}><div className="text-xs text-slate-400">{l}</div><div className={`text-lg font-bold ${c}`}>{v}</div></div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 justify-between">
        <div className="flex gap-2">
          {canEdit && <button className={BTN_SEC} onClick={() => setTareas((l) => [...l, { nombre: "", nivel: 0, inicio: "", fin: "", avance_pct: 0 }])}><Plus className="w-4 h-4" /> Tarea</button>}
          {canEdit && <button className={BTN_SEC} onClick={desdePres}><FileText className="w-4 h-4" /> Traer rubros del presupuesto</button>}
        </div>
        {canEdit && <button className={BTN_PRI} onClick={guardar} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar cronograma</button>}
      </div>

      <div className={`${CARD} overflow-x-auto`}>
        <table className="w-full text-sm min-w-[1000px]">
          <thead><tr className="text-xs text-slate-400 text-left border-b border-white/10">
            <th className="px-2 py-2 w-16">Cód.</th><th className="px-2 py-2">Tarea</th><th className="px-2 py-2 w-16">Nivel</th><th className="px-2 py-2 w-36">Inicio plan</th><th className="px-2 py-2 w-36">Fin plan</th>
            <th className="px-2 py-2 w-20 text-right">Avance %</th><th className="px-2 py-2 w-36">Fin real</th><th className="px-2 py-2 w-24">Estado</th><th className="px-2 py-2 w-16 text-right">Atraso</th><th className="px-2 py-2 w-[26%]">Línea de tiempo</th><th className="w-8"></th></tr></thead>
          <tbody>
            {tareas.length === 0 && <tr><td colSpan={11} className="px-3 py-8 text-center text-slate-500">Sin tareas. Armá el cronograma de la obra.</td></tr>}
            {tareas.map((t, i) => {
              const est = ESTADO_T[t.estado] || ESTADO_T.pendiente;
              const p = pos(t);
              return (
                <tr key={t.id || i} className="border-b border-white/5">
                  <td className="px-2 py-1"><input disabled={!canEdit} className={INPUT} value={t.codigo || ""} onChange={(e) => upd(i, { codigo: e.target.value })} /></td>
                  <td className="px-2 py-1"><input disabled={!canEdit} className={`${INPUT} ${t.nivel ? "ml-4 !w-[calc(100%-1rem)]" : "font-semibold"}`} value={t.nombre} onChange={(e) => upd(i, { nombre: e.target.value })} /></td>
                  <td className="px-2 py-1"><select disabled={!canEdit} className={INPUT} value={t.nivel || 0} onChange={(e) => upd(i, { nivel: Number(e.target.value) })}><option value={0}>Etapa</option><option value={1}>Sub</option></select></td>
                  <td className="px-2 py-1"><DateInput disabled={!canEdit} className={INPUT} value={t.inicio || ""} onChange={(e) => upd(i, { inicio: e.target.value })} /></td>
                  <td className="px-2 py-1"><DateInput disabled={!canEdit} className={INPUT} value={t.fin || ""} onChange={(e) => upd(i, { fin: e.target.value })} /></td>
                  <td className="px-2 py-1"><input disabled={!canEdit} type="number" min="0" max="100" className={`${INPUT} text-right`} value={t.avance_pct ?? 0} onChange={(e) => upd(i, { avance_pct: e.target.value })} /></td>
                  <td className="px-2 py-1"><DateInput disabled={!canEdit} className={INPUT} value={t.fin_real || ""} onChange={(e) => upd(i, { fin_real: e.target.value })} /></td>
                  <td className="px-2 py-1">{t.estado && <span className={`text-[11px] px-2 py-0.5 rounded-full ${est[1]}`}>{est[0]}</span>}</td>
                  <td className={`px-2 py-1 text-right ${t.atraso_dias > 0 ? "text-rose-400" : "text-slate-500"}`}>{t.atraso_dias ? `${t.atraso_dias} d` : "—"}</td>
                  <td className="px-2 py-1"><div className="relative h-4 bg-white/5 rounded">{p && <div className={`absolute top-0 h-4 rounded ${t.estado === "atrasada" ? "bg-rose-500/70" : t.estado === "cumplida" ? "bg-emerald-500/70" : "bg-blue-500/70"}`} style={p}><div className="h-full bg-white/30 rounded" style={{ width: `${t.avance_pct || 0}%` }} /></div>}</div></td>
                  <td>{canEdit && <button className="text-rose-400" onClick={() => setTareas((l) => l.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className={`${CARD} p-4 space-y-3`}>
          <div className="flex items-center gap-2 text-sm font-semibold text-white"><CloudRain className="w-4 h-4 text-sky-400" /> Días de lluvia / clima ({data?.dias_lluvia || 0} días de lluvia)</div>
          {canClima && (
            <div className="grid grid-cols-[1fr,70px,110px] gap-2">
              <DateInput className={INPUT} value={clima.fecha} onChange={(e) => setClima({ ...clima, fecha: e.target.value })} />
              <input type="number" step="0.5" min="0.5" className={INPUT} value={clima.dias} onChange={(e) => setClima({ ...clima, dias: e.target.value })} />
              <select className={INPUT} value={clima.tipo} onChange={(e) => setClima({ ...clima, tipo: e.target.value })}><option value="lluvia">Lluvia</option><option value="feriado">Feriado</option><option value="otro">Otro</option></select>
              <input className={`${INPUT} col-span-2`} placeholder="Notas" value={clima.notas} onChange={(e) => setClima({ ...clima, notas: e.target.value })} />
              <button className={BTN_PRI} onClick={async () => { try { await api(`/admin/constructora/proyectos/${proyecto.id}/clima`, { method: "POST", body: { ...clima, dias: Number(clima.dias) || 1 } }); setClima({ ...clima, notas: "" }); cargar(); } catch (e) { toast.error(e.message); } }}><Plus className="w-4 h-4" /></button>
            </div>
          )}
          <div className="max-h-48 overflow-y-auto divide-y divide-white/5 text-sm">
            {(data?.clima || []).map((c) => (
              <div key={c.id} className="flex items-center gap-2 py-1.5 text-slate-300"><span className="w-24">{c.fecha}</span><span className="capitalize w-16">{c.tipo}</span><span className="w-12">{c.dias} d</span><span className="flex-1 text-slate-500 truncate">{c.notas}</span>
                {canClima && <button className="text-rose-400" onClick={async () => { await api(`/admin/constructora/proyectos/${proyecto.id}/clima/${c.id}`, { method: "DELETE" }); cargar(); }}><Trash2 className="w-3.5 h-3.5" /></button>}</div>
            ))}
            {!(data?.clima || []).length && <div className="text-slate-500 py-3 text-center">Sin eventos registrados.</div>}
          </div>
        </div>
        <div className={`${CARD} p-4 space-y-2`}>
          <div className="text-sm font-semibold text-white">Historial de reprogramaciones</div>
          <div className="max-h-56 overflow-y-auto divide-y divide-white/5 text-xs text-slate-300">
            {(data?.reprogramaciones || []).slice().reverse().map((x, i) => (
              <div key={i} className="py-1.5"><b>{x.tarea}</b>: {x.de_inicio || "-"} → {x.de_fin || "-"} <span className="text-slate-500">pasó a</span> {x.a_inicio || "-"} → {x.a_fin || "-"}<div className="text-slate-500">{(x.fecha || "").slice(0, 10)} · {x.usuario}{x.motivo ? ` · ${x.motivo}` : ""}</div></div>
            ))}
            {!(data?.reprogramaciones || []).length && <div className="text-slate-500 py-3 text-center">Sin reprogramaciones.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// COMPRAS / GASTOS (Egresos embebido, acotado al proyecto)
// ═════════════════════════════════════════════════════════════════════════════

export function ComprasGastosTab({ proyecto, tipo }) {   // tipo: "compras" | "costos"
  const api = useApi();
  const can = useCan();
  const { activeEmpresaPropia } = useContext(AuthContext);
  const esCompra = tipo === "compras";
  const modPerm = esCompra ? "compras" : "costos_fijos";
  const secc = esCompra ? "constructora_compras" : "constructora_gastos";
  const canCrear = can(`${secc}.crear`) && can(`${modPerm}.crear`);
  const [lista, setLista] = useState([]);
  const [overlay, setOverlay] = useState(null); // null | { nuevo }
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const lt = activeEmpresaPropia?.slug ? `&logo_tipo=${activeEmpresaPropia.slug}` : "";
      setLista(await api(`/admin/${esCompra ? "compras" : "costos-fijos"}?proyecto_constructora_id=${proyecto.id}${lt}`));
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [api, proyecto.id, esCompra, activeEmpresaPropia?.slug]);
  useEffect(() => { cargar(); }, [cargar]);

  const total = lista.reduce((a, c) => a + (Number(esCompra ? c.monto_total : c.monto) || 0), 0);
  const m = proyecto.moneda || "PYG";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-slate-300">{lista.length} {esCompra ? "compras" : "gastos"} · total <b className="text-white">{fmtMon(total, m)}</b></div>
        <div className="flex gap-2">
          {canCrear && <button className={BTN_PRI} onClick={() => setOverlay({ nuevo: esCompra ? "compra" : "gasto" })}><Plus className="w-4 h-4" /> {esCompra ? "Nueva compra" : "Nuevo gasto"}</button>}
          <button className={BTN_SEC} onClick={() => setOverlay({ nuevo: null })}>{esCompra ? <ShoppingCart className="w-4 h-4" /> : <Receipt className="w-4 h-4" />} Gestionar / pagar</button>
        </div>
      </div>
      <div className={`${CARD} overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead><tr className="text-xs text-slate-400 text-left border-b border-white/10">
            {esCompra
              ? <><th className="px-3 py-2">Fecha</th><th className="px-3 py-2">Proveedor</th><th className="px-3 py-2">Factura</th><th className="px-3 py-2">Grupo</th><th className="px-3 py-2 text-right">Monto</th><th className="px-3 py-2">Estado</th></>
              : <><th className="px-3 py-2">Gasto</th><th className="px-3 py-2">Categoría</th><th className="px-3 py-2">Grupo</th><th className="px-3 py-2">Frecuencia</th><th className="px-3 py-2 text-right">Monto</th></>}
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-3 py-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-arandu-blue" /></td></tr>}
            {!loading && lista.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">Todavía no hay {esCompra ? "compras" : "gastos"} cargados a este proyecto.</td></tr>}
            {lista.map((c) => esCompra ? (
              <tr key={c.id} className="border-b border-white/5 text-slate-200">
                <td className="px-3 py-2">{c.fecha}</td><td className="px-3 py-2">{c.proveedor_nombre}</td><td className="px-3 py-2">{c.numero_factura || "—"}</td>
                <td className="px-3 py-2">{c.grupo_constructora_nombre || <span className="text-slate-500">Sin grupo</span>}</td>
                <td className="px-3 py-2 text-right">{fmtMon(c.monto_total, c.moneda || m)}</td><td className="px-3 py-2 capitalize">{c.estado_pago || "—"}</td>
              </tr>
            ) : (
              <tr key={c.id} className="border-b border-white/5 text-slate-200">
                <td className="px-3 py-2">{c.nombre}</td><td className="px-3 py-2">{c.categoria}</td><td className="px-3 py-2">{c.grupo_constructora_nombre || <span className="text-slate-500">Sin grupo</span>}</td>
                <td className="px-3 py-2 capitalize">{c.frecuencia}</td><td className="px-3 py-2 text-right">{fmtMon(c.monto, c.moneda || m)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {overlay && (
        <div className="fixed inset-0 z-40 bg-arandu-dark overflow-y-auto">
          <EgresosPage embedded proyectoCtx={{ id: proyecto.id, nombre: proyecto.nombre, numero: proyecto.numero }} lockTab={tipo} initialNuevo={overlay.nuevo}
            onEmbeddedClose={() => { setOverlay(null); cargar(); }} />
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// EQUIPOS
// ═════════════════════════════════════════════════════════════════════════════

export function EquiposTab({ proyecto }) {
  const api = useApi();
  const can = useCan();
  const { activeEmpresaPropia } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [activos, setActivos] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [conflictos, setConflictos] = useState([]);
  const [q, setQ] = useState("");

  const cargar = useCallback(async () => {
    try { setItems(await api(`/admin/constructora/proyectos/${proyecto.id}/equipos`)); } catch (e) { toast.error(e.message); }
  }, [api, proyecto.id]);
  useEffect(() => { cargar(); }, [cargar]);

  const abrir = async (item) => {
    setForm(item ? { ...item } : { activo_id: "", desde: hoy(), hasta: "", notas: "" });
    setConflictos([]); setModal(item || {});
    if (!activos.length) {
      try { setActivos(await api(`/admin/constructora/equipos/activos${activeEmpresaPropia?.slug ? `?logo_tipo=${activeEmpresaPropia.slug}` : ""}`)); } catch (e) { toast.error(e.message); }
    }
  };

  useEffect(() => {
    if (!modal || !form.activo_id || !form.desde) { setConflictos([]); return; }
    api(`/admin/constructora/equipos/disponibilidad?activo_id=${form.activo_id}&desde=${form.desde}${form.hasta ? `&hasta=${form.hasta}` : ""}${modal.id ? `&excluir_id=${modal.id}` : ""}`)
      .then((r) => setConflictos(r.conflictos || [])).catch(() => {});
  }, [form.activo_id, form.desde, form.hasta]); // eslint-disable-line

  const guardar = async (confirmar = false) => {
    try {
      const body = { activo_id: form.activo_id, desde: form.desde, hasta: form.hasta || null, notas: form.notas, confirmar_conflicto: confirmar };
      await api(modal.id ? `/admin/constructora/proyectos/${proyecto.id}/equipos/${modal.id}` : `/admin/constructora/proyectos/${proyecto.id}/equipos`, { method: modal.id ? "PUT" : "POST", body });
      toast.success("Equipo guardado"); setModal(null); cargar();
    } catch (e) {
      if (e.status === 409) {
        const c = e.data?.detail?.conflictos || [];
        if (window.confirm(`${e.message}:\n${c.map((x) => `• ${x.proyecto_numero || ""} ${x.proyecto_nombre} (${x.desde} → ${x.hasta || "sin fin"})`).join("\n")}\n\n¿Asignarlo igual (uso compartido)?`)) guardar(true);
      } else toast.error(e.message);
    }
  };

  const filtrados = activos.filter((a) => !q.trim() || `${a.nombre} ${a.categoria} ${a.placa || ""}`.toLowerCase().includes(q.toLowerCase())).slice(0, 40);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-400">Equipos y maquinarias del Inventario Técnico usados en esta obra.</div>
        {can("constructora_equipos.asignar") && <button className={BTN_PRI} onClick={() => abrir(null)}><Plus className="w-4 h-4" /> Asignar equipo</button>}
      </div>
      <div className={`${CARD} overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead><tr className="text-xs text-slate-400 text-left border-b border-white/10"><th className="px-3 py-2">Equipo</th><th className="px-3 py-2">Categoría</th><th className="px-3 py-2">Desde</th><th className="px-3 py-2">Hasta</th><th className="px-3 py-2 text-right">Días</th><th className="px-3 py-2">Uso en otros proyectos</th><th className="w-20"></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">Sin equipos asignados.</td></tr>}
            {items.map((e) => (
              <tr key={e.id} className="border-b border-white/5 text-slate-200">
                <td className="px-3 py-2 font-medium">{e.activo_nombre}</td><td className="px-3 py-2">{e.categoria || "—"}</td><td className="px-3 py-2">{e.desde}</td><td className="px-3 py-2">{e.hasta || <span className="text-emerald-400">en uso</span>}</td><td className="px-3 py-2 text-right">{e.dias}</td>
                <td className="px-3 py-2">{e.conflictos?.length ? <span className="text-amber-400 flex items-center gap-1 text-xs"><AlertTriangle className="w-3.5 h-3.5" />{e.conflictos.map((c) => `${c.proyecto_numero || ""} ${c.proyecto_nombre}`).join(", ")}</span> : <span className="text-slate-500">—</span>}</td>
                <td className="px-3 py-2 flex gap-2">
                  {can("constructora_equipos.editar") && <button className="text-slate-400 hover:text-white" onClick={() => abrir(e)}><Pencil className="w-4 h-4" /></button>}
                  {can("constructora_equipos.eliminar") && <button className="text-rose-400" onClick={async () => { if (window.confirm("¿Quitar este equipo de la obra?")) { await api(`/admin/constructora/proyectos/${proyecto.id}/equipos/${e.id}`, { method: "DELETE" }); cargar(); } }}><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={modal.id ? "Editar asignación" : "Asignar equipo"} onClose={() => setModal(null)}>
          <div className="space-y-3">
            {!modal.id && (
              <div>
                <label className={LABEL}>Equipo (Inventario Técnico)</label>
                <input className={INPUT} placeholder="Buscar camión, camioneta, retro…" value={q} onChange={(e) => setQ(e.target.value)} />
                <div className="mt-1 max-h-44 overflow-y-auto border border-white/10 rounded-lg divide-y divide-white/5">
                  {filtrados.map((a) => (
                    <button key={a.id} type="button" onClick={() => setForm({ ...form, activo_id: a.id })} className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 ${form.activo_id === a.id ? "bg-arandu-blue/20 text-white" : "text-slate-300"}`}>
                      {a.nombre} <span className="text-slate-500 text-xs">{a.categoria}{a.placa ? ` · ${a.placa}` : ""}</span>
                      {a.en_uso_hoy?.length > 0 && <span className="ml-2 text-[11px] text-amber-400">en uso hoy: {a.en_uso_hoy.join(", ")}</span>}
                    </button>
                  ))}
                  {!filtrados.length && <div className="px-3 py-3 text-xs text-slate-500">No hay equipos cargados en Inventario Técnico para esta empresa.</div>}
                </div>
              </div>
            )}
            {modal.id && <div className="text-sm text-white font-medium">{modal.activo_nombre}</div>}
            <div className="grid grid-cols-2 gap-3">
              <div><label className={LABEL}>Desde</label><DateInput className={INPUT} value={form.desde || ""} onChange={(e) => setForm({ ...form, desde: e.target.value })} /></div>
              <div><label className={LABEL}>Hasta (vacío = sin fecha de fin)</label><DateInput className={INPUT} value={form.hasta || ""} onChange={(e) => setForm({ ...form, hasta: e.target.value })} /></div>
            </div>
            <div><label className={LABEL}>Notas</label><input className={INPUT} value={form.notas || ""} onChange={(e) => setForm({ ...form, notas: e.target.value })} /></div>
            {conflictos.length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-300 space-y-1">
                <div className="flex items-center gap-1 font-semibold"><AlertTriangle className="w-4 h-4" /> Ya está asignado a otro proyecto en esas fechas:</div>
                {conflictos.map((c) => <div key={c.id}>• {c.proyecto_numero} {c.proyecto_nombre} ({c.desde} → {c.hasta || "sin fin"})</div>)}
                <div className="text-amber-200/70">Podés asignarlo igual: quedará marcado como uso compartido.</div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className={BTN_SEC} onClick={() => setModal(null)}>Cancelar</button>
            <button className={BTN_PRI} disabled={!form.activo_id && !modal.id} onClick={() => guardar(conflictos.length > 0)}><Save className="w-4 h-4" /> Guardar</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PERSONAL + PAGOS CON RECIBO
// ═════════════════════════════════════════════════════════════════════════════

function imprimirRecibo(pg, proyecto, empresa) {
  const w = window.open("", "_blank");
  if (!w) return;
  const m = pg.moneda || "PYG";
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Recibo ${pg.recibo_numero}</title>
  <style>body{font-family:Arial,sans-serif;padding:30px;color:#111;font-size:13px}.box{border:2px solid #111;padding:22px;max-width:640px;margin:auto}
  h1{font-size:18px;margin:0}.row{display:flex;justify-content:space-between;margin:6px 0}.firmas{display:flex;gap:30px;margin-top:70px}.f{flex:1;border-top:1px solid #111;text-align:center;padding-top:6px;font-size:11px}
  @media print{.np{display:none}}</style></head><body>
  <div class="np" style="text-align:right;margin-bottom:10px"><button onclick="window.print()">Imprimir</button></div>
  <div class="box"><div class="row"><div><h1>${empresa || ""}</h1><div>Proyecto: ${proyecto.numero} — ${proyecto.nombre}</div></div><div style="text-align:right"><b>RECIBO N° ${pg.recibo_numero}</b><div>Fecha: ${pg.fecha}</div></div></div><hr/>
  <p>Recibí de <b>${empresa || ""}</b> la suma de <b>${fmtMon(pg.monto, m)}</b> en concepto de <b>${pg.concepto || "pago de personal"}</b>.</p>
  <div class="row"><span>Recibido por:</span><b>${pg.empleado_nombre || ""}</b></div>
  ${pg.empleado_documento ? `<div class="row"><span>C.I. / RUC:</span><b>${pg.empleado_documento}</b></div>` : ""}
  ${pg.cuenta_nombre ? `<div class="row"><span>Cuenta de pago:</span><span>${pg.cuenta_nombre}</span></div>` : ""}
  <div class="firmas"><div class="f">Firma del empleado</div><div class="f">Aclaración / C.I.</div><div class="f">Por la empresa</div></div></div></body></html>`);
  w.document.close();
}

export function PersonalTab({ proyecto }) {
  const api = useApi();
  const can = useCan();
  const { token, activeEmpresaPropia } = useContext(AuthContext);
  const slug = activeEmpresaPropia?.slug || "";
  const [items, setItems] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [modal, setModal] = useState(null);
  const [pagoModal, setPagoModal] = useState(null);
  const [form, setForm] = useState({});
  const [pagoForm, setPagoForm] = useState({});
  const [nuevoEmp, setNuevoEmp] = useState(false);
  const [abierto, setAbierto] = useState(null);
  const m = proyecto.moneda || "PYG";

  const cargar = useCallback(async () => {
    try { setItems(await api(`/admin/constructora/proyectos/${proyecto.id}/personal`)); } catch (e) { toast.error(e.message); }
  }, [api, proyecto.id]);
  useEffect(() => { cargar(); }, [cargar]);

  const cargarEmpleados = async () => {
    try { setEmpleados(await api(`/admin/entidades?es_empleado=true${slug ? `&logo_tipo=${slug}` : ""}`)); } catch { /* noop */ }
  };

  const abrirAsignar = async (it) => {
    setForm(it ? { ...it } : { empleado_id: "", rol: "", desde: hoy(), hasta: "", tipo_pago: "jornal", tarifa: "", notas: "" });
    setModal(it || {});
    if (!it) cargarEmpleados();
  };
  const guardar = async () => {
    try {
      const body = { empleado_id: form.empleado_id, rol: form.rol, desde: form.desde, hasta: form.hasta || null, tipo_pago: form.tipo_pago, tarifa: Number(form.tarifa) || 0, notas: form.notas };
      await api(modal.id ? `/admin/constructora/proyectos/${proyecto.id}/personal/${modal.id}` : `/admin/constructora/proyectos/${proyecto.id}/personal`, { method: modal.id ? "PUT" : "POST", body });
      toast.success("Guardado"); setModal(null); cargar();
    } catch (e) { toast.error(e.message); }
  };

  const abrirPago = async (it) => {
    setPagoForm({ fecha: hoy(), monto: it.tarifa || "", concepto: it.tipo_pago === "mensual" ? "Sueldo" : "Jornal", cuenta_id: "", grupo_constructora_id: "" });
    setPagoModal(it);
    try {
      const [c, g] = await Promise.all([api(`/admin/cuentas-bancarias${slug ? `?logo_tipo=${slug}` : ""}`), can("constructora_configuracion.ver") || can("constructora_proyectos.ver") ? api(`/admin/constructora/grupos?solo_activos=true${slug ? `&logo_tipo=${slug}` : ""}`) : []]);
      setCuentas(c); setGrupos(g);
    } catch { /* noop */ }
  };
  const pagar = async () => {
    try {
      const cta = cuentas.find((c) => c.id === pagoForm.cuenta_id);
      const gr = grupos.find((g) => g.id === pagoForm.grupo_constructora_id);
      const pg = await api(`/admin/constructora/proyectos/${proyecto.id}/personal/${pagoModal.id}/pagos`, { method: "POST", body: {
        fecha: pagoForm.fecha, monto: Number(pagoForm.monto) || 0, moneda: cta?.moneda || m, concepto: pagoForm.concepto,
        cuenta_id: pagoForm.cuenta_id || null, grupo_constructora_id: gr?.id || null, grupo_constructora_nombre: gr?.nombre || null,
      } });
      toast.success(`Pago registrado — recibo ${pg.recibo_numero}`);
      setPagoModal(null); cargar();
      imprimirRecibo(pg, proyecto, activeEmpresaPropia?.nombre);
    } catch (e) { toast.error(e.message); }
  };

  const totalPagado = items.reduce((a, i) => a + (i.total_pagado || 0), 0);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-300">{items.length} personas · pagado <b className="text-white">{fmtMon(totalPagado, m)}</b></div>
        {can("constructora_personal.asignar") && <button className={BTN_PRI} onClick={() => abrirAsignar(null)}><Plus className="w-4 h-4" /> Asignar personal</button>}
      </div>
      <div className={`${CARD} overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead><tr className="text-xs text-slate-400 text-left border-b border-white/10"><th className="px-3 py-2">Empleado</th><th className="px-3 py-2">Rol</th><th className="px-3 py-2">Desde</th><th className="px-3 py-2">Hasta</th><th className="px-3 py-2">Pago</th><th className="px-3 py-2 text-right">Tarifa</th><th className="px-3 py-2 text-right">Pagado</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">Sin personal asignado.</td></tr>}
            {items.map((it) => (
              <React.Fragment key={it.id}>
                <tr className="border-b border-white/5 text-slate-200">
                  <td className="px-3 py-2 font-medium">{it.empleado_nombre}</td><td className="px-3 py-2">{it.rol || "—"}</td><td className="px-3 py-2">{it.desde}</td>
                  <td className="px-3 py-2">{it.hasta || <span className="text-emerald-400">activo</span>}</td><td className="px-3 py-2 capitalize">{it.tipo_pago}</td>
                  <td className="px-3 py-2 text-right">{fmtMon(it.tarifa, m)}</td><td className="px-3 py-2 text-right">{fmtMon(it.total_pagado, m)}</td>
                  <td className="px-3 py-2 flex gap-2 justify-end">
                    {can("constructora_personal.pagar") && <button className={BTN_SEC} onClick={() => abrirPago(it)}><DollarSign className="w-4 h-4" /> Pagar</button>}
                    <button className="text-slate-400 hover:text-white text-xs underline" onClick={() => setAbierto(abierto === it.id ? null : it.id)}>{it.pagos.length} pagos</button>
                    {can("constructora_personal.editar") && <button className="text-slate-400 hover:text-white" onClick={() => abrirAsignar(it)}><Pencil className="w-4 h-4" /></button>}
                    {can("constructora_personal.eliminar") && <button className="text-rose-400" onClick={async () => { if (window.confirm("¿Quitar de la obra?")) { try { await api(`/admin/constructora/proyectos/${proyecto.id}/personal/${it.id}`, { method: "DELETE" }); cargar(); } catch (e) { toast.error(e.message); } } }}><Trash2 className="w-4 h-4" /></button>}
                  </td>
                </tr>
                {abierto === it.id && (
                  <tr><td colSpan={8} className="bg-white/[0.03] px-6 py-2">
                    {it.pagos.length === 0 ? <div className="text-xs text-slate-500">Sin pagos.</div> : it.pagos.map((p) => (
                      <div key={p.id} className="flex items-center gap-3 text-xs text-slate-300 py-1">
                        <span className="w-20">{p.fecha}</span><span className="w-24 font-mono">{p.recibo_numero}</span><span className="flex-1">{p.concepto}</span><span className="w-32 text-right">{fmtMon(p.monto, p.moneda)}</span>
                        <button className="text-slate-400 hover:text-white" title="Imprimir recibo" onClick={() => imprimirRecibo({ ...p, empleado_documento: it.empleado_documento }, proyecto, activeEmpresaPropia?.nombre)}><Printer className="w-4 h-4" /></button>
                        {can("constructora_personal.pagar") && <button className="text-rose-400" title="Anular pago" onClick={async () => { if (window.confirm(`¿Anular el pago ${p.recibo_numero}? Se revierte el egreso de la cuenta.`)) { await api(`/admin/constructora/proyectos/${proyecto.id}/personal/pagos/${p.id}`, { method: "DELETE" }); cargar(); } }}><Trash2 className="w-3.5 h-3.5" /></button>}
                      </div>
                    ))}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={modal.id ? "Editar personal" : "Asignar personal a la obra"} onClose={() => setModal(null)}>
          <div className="space-y-3">
            {!modal.id ? (
              <div>
                <label className={LABEL}>Empleado</label>
                <div className="flex gap-2">
                  <select className={INPUT} value={form.empleado_id} onChange={(e) => setForm({ ...form, empleado_id: e.target.value })}>
                    <option value="">Seleccionar…</option>
                    {empleados.map((e) => <option key={e.id} value={e.id}>{`${e.nombre || ""} ${e.apellido || ""}`.trim()}</option>)}
                  </select>
                  <button type="button" className={BTN_SEC} onClick={() => setNuevoEmp(true)}><Plus className="w-4 h-4" /> Nuevo</button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Si no está en Empleados, creálo acá y queda registrado en el Catastro.</p>
              </div>
            ) : <div className="text-white font-medium">{modal.empleado_nombre}</div>}
            <div className="grid grid-cols-2 gap-3">
              <div><label className={LABEL}>Rol / oficio</label><input className={INPUT} value={form.rol || ""} onChange={(e) => setForm({ ...form, rol: e.target.value })} placeholder="Albañil, capataz…" /></div>
              <div><label className={LABEL}>Tipo de pago</label><select className={INPUT} value={form.tipo_pago} onChange={(e) => setForm({ ...form, tipo_pago: e.target.value })}><option value="jornal">Jornal</option><option value="mensual">Mensual</option><option value="destajo">Destajo</option></select></div>
              <div><label className={LABEL}>Desde</label><DateInput className={INPUT} value={form.desde || ""} onChange={(e) => setForm({ ...form, desde: e.target.value })} /></div>
              <div><label className={LABEL}>Hasta (se puede completar después)</label><DateInput className={INPUT} value={form.hasta || ""} onChange={(e) => setForm({ ...form, hasta: e.target.value })} /></div>
              <div><label className={LABEL}>Tarifa ({form.tipo_pago === "jornal" ? "por día" : form.tipo_pago === "mensual" ? "por mes" : "por trabajo"})</label><input type="number" className={INPUT} value={form.tarifa ?? ""} onChange={(e) => setForm({ ...form, tarifa: e.target.value })} /></div>
              <div><label className={LABEL}>Notas</label><input className={INPUT} value={form.notas || ""} onChange={(e) => setForm({ ...form, notas: e.target.value })} /></div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5"><button className={BTN_SEC} onClick={() => setModal(null)}>Cancelar</button><button className={BTN_PRI} disabled={!form.empleado_id} onClick={guardar}><Save className="w-4 h-4" /> Guardar</button></div>
        </Modal>
      )}
      {nuevoEmp && (
        <EntidadModal
          entidad={{ _presetRol: "es_empleado" }} logo={slug} token={token}
          allowedRoles={{ es_empleado: true, es_cliente: false, es_proveedor: false }}
          onClose={() => setNuevoEmp(false)}
          onSaved={async (nuevo) => { setNuevoEmp(false); await cargarEmpleados(); if (nuevo?.id) setForm((f) => ({ ...f, empleado_id: nuevo.id })); }}
        />
      )}
      {pagoModal && (
        <Modal title={`Pagar a ${pagoModal.empleado_nombre}`} onClose={() => setPagoModal(null)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className={LABEL}>Fecha</label><DateInput className={INPUT} value={pagoForm.fecha} onChange={(e) => setPagoForm({ ...pagoForm, fecha: e.target.value })} /></div>
              <div><label className={LABEL}>Monto</label><input type="number" className={INPUT} value={pagoForm.monto} onChange={(e) => setPagoForm({ ...pagoForm, monto: e.target.value })} /></div>
            </div>
            <div><label className={LABEL}>Concepto</label><input className={INPUT} value={pagoForm.concepto} onChange={(e) => setPagoForm({ ...pagoForm, concepto: e.target.value })} placeholder="Jornal semana 1, quincena, adelanto…" /></div>
            <div><label className={LABEL}>Cuenta / caja de donde sale</label>
              <select className={INPUT} value={pagoForm.cuenta_id} onChange={(e) => setPagoForm({ ...pagoForm, cuenta_id: e.target.value })}><option value="">Cuenta predeterminada</option>{cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre} ({c.moneda})</option>)}</select></div>
            <div><label className={LABEL}>Grupo de gasto (para reportes)</label>
              <select className={INPUT} value={pagoForm.grupo_constructora_id} onChange={(e) => setPagoForm({ ...pagoForm, grupo_constructora_id: e.target.value })}><option value="">Mano de obra (por defecto)</option>{grupos.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}</select></div>
          </div>
          <div className="flex justify-end gap-2 mt-5"><button className={BTN_SEC} onClick={() => setPagoModal(null)}>Cancelar</button><button className={BTN_PRI} onClick={pagar}><Save className="w-4 h-4" /> Pagar e imprimir recibo</button></div>
        </Modal>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// FACTURACIÓN + ANTICIPOS
// ═════════════════════════════════════════════════════════════════════════════

export function FacturacionTab({ proyecto, clientes, openSignal }) {
  const api = useApi();
  const can = useCan();
  const { token, hasPermission, hasModule, activeEmpresaPropia } = useContext(AuthContext);
  const slug = activeEmpresaPropia?.slug || "";
  const m = proyecto.moneda || "PYG";
  const [facturas, setFacturas] = useState([]);
  const [anticipos, setAnticipos] = useState({ anticipos: [], total: 0, aplicado: 0, disponible: 0 });
  const [cuentas, setCuentas] = useState([]);
  const [planCuentas, setPlanCuentas] = useState([]);
  const [pres, setPres] = useState(null);
  const [antModal, setAntModal] = useState(null);
  const [antForm, setAntForm] = useState({});
  const [paso, setPaso] = useState(null);      // null | { sinFactura }  → diálogo de origen/monto/anticipos
  const [plan, setPlan] = useState({ origen: "libre", cuota: "", pct: "", monto: "", texto: "", mes: hoy().slice(0, 7), usar: {} });
  const [factForm, setFactForm] = useState(null); // { initial, sinFactura, aplicar:[{vale_id,monto}] }

  const canVerFac = can("constructora_facturacion.ver") && can("facturas.ver");
  const canFacturar = can("constructora_facturacion.facturar") && can("facturas.crear");
  const canAnt = can("constructora_anticipos.ver");

  const cargar = useCallback(async () => {
    try {
      if (canVerFac) setFacturas(await api(`/admin/facturas?proyecto_constructora_id=${proyecto.id}${slug ? `&logo_tipo=${slug}` : ""}&incluir_anuladas=true`));
      if (canAnt) setAnticipos(await api(`/admin/constructora/proyectos/${proyecto.id}/anticipos`));
    } catch (e) { toast.error(e.message); }
  }, [api, proyecto.id, slug, canVerFac, canAnt]);
  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    fetch(`${API}/admin/cuentas-bancarias${slug ? `?logo_tipo=${slug}` : ""}`, { headers: h }).then((r) => r.ok ? r.json() : []).then(setCuentas).catch(() => {});
    fetch(`${API}/admin/plan-cuentas${slug ? `?logo_tipo=${slug}` : ""}`, { headers: h }).then((r) => r.ok ? r.json() : []).then(setPlanCuentas).catch(() => {});
    if (can("constructora_presupuesto.ver")) api(`/admin/constructora/proyectos/${proyecto.id}/presupuesto`).then(setPres).catch(() => {});
  }, [token, slug, proyecto.id]); // eslint-disable-line

  const textoBase = useCallback((mes) => {
    const [y, mm] = (mes || hoy().slice(0, 7)).split("-");
    const plantilla = pres?.concepto_factura || "Avance de obra en proyecto {proyecto} — {mes}";
    return plantilla.replace("{proyecto}", proyecto.nombre || "").replace("{mes}", `${MESES[Number(mm) - 1] || ""} ${y}`).replace("{cliente}", proyecto.cliente_nombre || "");
  }, [pres, proyecto]);

  const abrirPaso = (sinFactura, origen = "libre") => {
    const usar = {};
    (anticipos.anticipos || []).filter((a) => a.estado !== "anulado" && a.monto_disponible > 0).forEach((a) => { usar[a.id] = true; });
    setPlan({ origen, cuota: "", pct: "", monto: "", texto: textoBase(hoy().slice(0, 7)), mes: hoy().slice(0, 7), usar });
    setPaso({ sinFactura });
  };
  // permite que otras pestañas (Presupuesto / Plan de cobros) abran el diálogo
  useEffect(() => { if (openSignal) abrirPaso(false, openSignal.origen); }, [openSignal]); // eslint-disable-line

  const montoPlan = () => {
    if (plan.origen === "cuota") return Number((proyecto.plan_cobros || []).find((c) => c.id === plan.cuota)?.monto) || 0;
    if (plan.origen === "pct") return Math.round((Number(pres?.total_con_iva) || Number(proyecto.monto_contrato) || 0) * (Number(plan.pct) || 0) / 100);
    return Number(plan.monto) || 0;
  };
  const dispTotal = (anticipos.anticipos || []).filter((a) => plan.usar[a.id]).reduce((s, a) => s + (a.monto_disponible || 0), 0);

  const continuar = () => {
    const monto = montoPlan();
    if (monto <= 0) { toast.error("Indicá el monto a facturar"); return; }
    // anticipos elegidos: se aplican en orden hasta cubrir el monto
    let resto = monto;
    const aplicar = [];
    (anticipos.anticipos || []).filter((a) => plan.usar[a.id] && a.monto_disponible > 0).forEach((a) => {
      const x = Math.min(a.monto_disponible, resto);
      if (x > 0) { aplicar.push({ vale_id: a.id, monto: x }); resto -= x; }
    });
    const cli = clientes.find((c) => c.id === proyecto.cliente_id);
    setFactForm({
      sinFactura: paso.sinFactura, aplicar,
      initial: {
        __source_id: `constructora-${proyecto.id}-${Date.now()}`,
        moneda: m, empresa_id: proyecto.cliente_id, _empresa_id: proyecto.cliente_id,
        razon_social: cli?.razon_social || cli?.nombre || proyecto.cliente_razon_social || proyecto.cliente_nombre || "",
        ruc: cli?.ruc || proyecto.cliente_ruc || "",
        proyecto_constructora_id: proyecto.id, proyecto_constructora_nombre: proyecto.nombre,
        proyecto_cuota_id: plan.origen === "cuota" ? (plan.cuota || null) : null,
        conceptos: [{ descripcion: plan.texto, cantidad: 1, precio_unitario: monto, subtotal: monto, iva_tipo: "10" }],
      },
    });
    setPaso(null);
  };

  const guardarAnticipo = async () => {
    try {
      const cta = cuentas.find((c) => c.id === antForm.cuenta_id);
      await api(`/admin/constructora/proyectos/${proyecto.id}/anticipos`, { method: "POST", body: { fecha: antForm.fecha, monto: Number(antForm.monto) || 0, moneda: cta?.moneda || m, cuenta_id: antForm.cuenta_id || null, notas: antForm.notas } });
      toast.success("Anticipo registrado"); setAntModal(null); cargar();
    } catch (e) { toast.error(e.message); }
  };

  const totFact = facturas.filter((f) => f.estado !== "anulada").reduce((a, f) => a + (Number(f.monto) || 0), 0);
  const totCob = facturas.filter((f) => f.estado !== "anulada").reduce((a, f) => a + (Number(f.monto_pagado) || 0), 0);
  const cuotasPend = (proyecto.plan_cobros || []).filter((c) => c.estado !== "cobrado");

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4 flex-1">
          {[["Facturado", fmtMon(totFact, m), "text-white"], ["Cobrado en facturas", fmtMon(totCob, m), "text-emerald-400"], ["Anticipos recibidos", fmtMon(anticipos.total, m), "text-sky-400"], ["Anticipo disponible", fmtMon(anticipos.disponible, m), "text-amber-400"]].map(([l, v, c]) => (
            <div key={l} className={`${CARD} p-3`}><div className="text-xs text-slate-400">{l}</div><div className={`font-bold ${c}`}>{v}</div></div>
          ))}
        </div>
        <div className="flex gap-2">
          {canFacturar && <button className={BTN_PRI} onClick={() => abrirPaso(false)}><Receipt className="w-4 h-4" /> Nueva factura</button>}
          {canFacturar && <button className={BTN_SEC} onClick={() => abrirPaso(true)}><FileText className="w-4 h-4" /> Nueva boleta</button>}
        </div>
      </div>

      {canAnt && (
        <div className={`${CARD} p-4 space-y-2`}>
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-white">Anticipos de obra</div>
            {can("constructora_anticipos.crear") && <button className={BTN_SEC} onClick={() => { setAntForm({ fecha: hoy(), monto: "", cuenta_id: "", notas: "" }); setAntModal(true); }}><Plus className="w-4 h-4" /> Registrar anticipo</button>}
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-slate-400 text-left"><th className="py-1">Fecha</th><th>Detalle</th><th className="text-right">Monto</th><th className="text-right">Aplicado</th><th className="text-right">Disponible</th><th>Estado</th><th></th></tr></thead>
            <tbody>
              {(anticipos.anticipos || []).map((a) => (
                <tr key={a.id} className="border-t border-white/5 text-slate-200">
                  <td className="py-1.5">{(a.created_at || "").slice(0, 10)}</td><td className="truncate max-w-[260px]">{a.ingreso_descripcion}</td>
                  <td className="text-right">{fmtMon(a.monto_total, a.moneda)}</td><td className="text-right">{fmtMon(a.monto_aplicado, a.moneda)}</td><td className="text-right">{fmtMon(a.monto_disponible, a.moneda)}</td>
                  <td className="capitalize">{a.estado}</td>
                  <td className="text-right">{can("constructora_anticipos.anular") && a.estado !== "anulado" && !(a.monto_aplicado > 0) && <button className="text-rose-400" onClick={async () => { if (window.confirm("¿Anular este anticipo? Se revierte el ingreso a la cuenta.")) { try { await api(`/admin/constructora/proyectos/${proyecto.id}/anticipos/${a.id}`, { method: "DELETE" }); cargar(); } catch (e) { toast.error(e.message); } } }}><Trash2 className="w-4 h-4" /></button>}</td>
                </tr>
              ))}
              {!(anticipos.anticipos || []).length && <tr><td colSpan={7} className="py-4 text-center text-slate-500">Sin anticipos registrados.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {canVerFac && (
        <div className={`${CARD} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-slate-400 text-left border-b border-white/10"><th className="px-3 py-2">Fecha</th><th className="px-3 py-2">N°</th><th className="px-3 py-2">Concepto</th><th className="px-3 py-2 text-right">Monto</th><th className="px-3 py-2 text-right">Cobrado</th><th className="px-3 py-2">Estado</th></tr></thead>
            <tbody>
              {facturas.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">Todavía no se emitieron facturas de este proyecto.</td></tr>}
              {facturas.map((f) => (
                <tr key={f.id} className={`border-b border-white/5 ${f.estado === "anulada" ? "text-slate-500 line-through" : "text-slate-200"}`}>
                  <td className="px-3 py-2">{f.fecha}</td><td className="px-3 py-2">{f.numero || f.numero_boleta || (f.sin_factura ? "Boleta" : "—")}</td><td className="px-3 py-2 truncate max-w-[320px]">{f.concepto}</td>
                  <td className="px-3 py-2 text-right">{fmtMon(f.monto, f.moneda)}</td><td className="px-3 py-2 text-right">{fmtMon(f.monto_pagado, f.moneda)}</td><td className="px-3 py-2 capitalize">{f.estado}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {paso && (
        <Modal title={paso.sinFactura ? "Nueva boleta del proyecto" : "Nueva factura del proyecto"} onClose={() => setPaso(null)} wide>
          <div className="space-y-4">
            <div>
              <label className={LABEL}>¿Qué se factura?</label>
              <div className="flex flex-wrap gap-2">
                {[["libre", "Monto libre"], ["pct", "% del presupuesto"], ["cuota", "Cuota del plan de cobros"]].map(([k, l]) => (
                  <button key={k} onClick={() => setPlan({ ...plan, origen: k })} className={`px-3 py-1.5 rounded-lg text-sm border ${plan.origen === k ? "bg-arandu-blue border-arandu-blue text-white" : "border-white/10 text-slate-300 bg-white/5"}`}>{l}</button>
                ))}
              </div>
            </div>
            {plan.origen === "libre" && <div><label className={LABEL}>Monto (con IVA)</label><input type="number" className={INPUT} value={plan.monto} onChange={(e) => setPlan({ ...plan, monto: e.target.value })} /></div>}
            {plan.origen === "pct" && (
              <div className="grid grid-cols-2 gap-3">
                <div><label className={LABEL}>% a facturar sobre {fmtMon(pres?.total_con_iva || proyecto.monto_contrato, m)}</label><input type="number" className={INPUT} value={plan.pct} onChange={(e) => setPlan({ ...plan, pct: e.target.value })} /></div>
                <div className="flex items-end text-sm text-emerald-400">= {fmtMon(montoPlan(), m)}</div>
              </div>
            )}
            {plan.origen === "cuota" && (
              <div><label className={LABEL}>Cuota</label>
                <select className={INPUT} value={plan.cuota} onChange={(e) => setPlan({ ...plan, cuota: e.target.value })}>
                  <option value="">Seleccionar…</option>
                  {cuotasPend.map((c) => <option key={c.id} value={c.id}>{c.concepto} — {fmtMon(c.monto, m)} ({c.fecha_prevista || "sin fecha"})</option>)}
                </select>
              </div>
            )}
            <div className="grid grid-cols-[150px,1fr] gap-3">
              <div><label className={LABEL}>Mes</label><input type="month" className={INPUT} value={plan.mes} onChange={(e) => setPlan({ ...plan, mes: e.target.value, texto: textoBase(e.target.value) })} /></div>
              <div><label className={LABEL}>Texto en la factura (editable)</label><input className={INPUT} value={plan.texto} onChange={(e) => setPlan({ ...plan, texto: e.target.value })} /></div>
            </div>
            {(anticipos.anticipos || []).some((a) => a.estado !== "anulado" && a.monto_disponible > 0) && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 space-y-2">
                <div className="text-sm text-amber-300 font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Este proyecto tiene anticipos disponibles</div>
                <div className="text-xs text-amber-200/80">Elegí cuáles descontar de esta factura (se aplican como cobro al guardarla). Si no marcás ninguno, quedan como anticipo para una próxima factura.</div>
                {(anticipos.anticipos || []).filter((a) => a.estado !== "anulado" && a.monto_disponible > 0).map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-sm text-slate-200">
                    <input type="checkbox" checked={!!plan.usar[a.id]} onChange={(e) => setPlan({ ...plan, usar: { ...plan.usar, [a.id]: e.target.checked } })} />
                    {(a.created_at || "").slice(0, 10)} — disponible <b>{fmtMon(a.monto_disponible, a.moneda)}</b>
                  </label>
                ))}
                <div className="text-xs text-slate-300">Se descontarán hasta {fmtMon(Math.min(dispTotal, montoPlan()), m)} de {fmtMon(montoPlan(), m)}.</div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-5"><button className={BTN_SEC} onClick={() => setPaso(null)}>Cancelar</button><button className={BTN_PRI} onClick={continuar}>Continuar a la factura</button></div>
        </Modal>
      )}

      {antModal && (
        <Modal title="Registrar anticipo de obra" onClose={() => setAntModal(null)}>
          <div className="space-y-3">
            <p className="text-xs text-slate-400">El dinero ingresa a la cuenta elegida y queda como crédito del cliente, aplicable a las facturas de este proyecto.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={LABEL}>Fecha</label><DateInput className={INPUT} value={antForm.fecha} onChange={(e) => setAntForm({ ...antForm, fecha: e.target.value })} /></div>
              <div><label className={LABEL}>Monto</label><input type="number" className={INPUT} value={antForm.monto} onChange={(e) => setAntForm({ ...antForm, monto: e.target.value })} /></div>
            </div>
            <div><label className={LABEL}>Cuenta donde ingresó</label><select className={INPUT} value={antForm.cuenta_id} onChange={(e) => setAntForm({ ...antForm, cuenta_id: e.target.value })}><option value="">Cuenta predeterminada</option>{cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre} ({c.moneda})</option>)}</select></div>
            <div><label className={LABEL}>Notas</label><input className={INPUT} value={antForm.notas} onChange={(e) => setAntForm({ ...antForm, notas: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-5"><button className={BTN_SEC} onClick={() => setAntModal(null)}>Cancelar</button><button className={BTN_PRI} onClick={guardarAnticipo}><Save className="w-4 h-4" /> Registrar</button></div>
        </Modal>
      )}

      {factForm && (
        <FacturaFormModal
          factura={null} sinFactura={factForm.sinFactura} token={token} API={API}
          empresas={clientes} presupuestosDisp={[]} activeEmpresaPropia={activeEmpresaPropia} hasPermission={hasPermission}
          productos={[]} productosHabilitados={false} cuentasDisp={cuentas} planCuentasDisp={planCuentas}
          initialValues={factForm.initial} defaultEmpresaId={proyecto.cliente_id || ""} canUseDescuento={false}
          onClose={() => setFactForm(null)}
          onSaved={async (saved) => {
            const aplicar = factForm.aplicar || [];
            setFactForm(null);
            if (saved?.id && aplicar.length) {
              try {
                await api(`/admin/constructora/proyectos/${proyecto.id}/anticipos/aplicar`, { method: "POST", body: { factura_id: saved.id, aplicaciones: aplicar } });
                toast.success("Anticipo aplicado a la factura");
              } catch (e) { toast.error(`Factura creada, pero no se pudo aplicar el anticipo: ${e.message}`); }
            }
            cargar();
          }}
        />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// REPORTES
// ═════════════════════════════════════════════════════════════════════════════

export function ReportesTab({ proyecto }) {
  const api = useApi();
  const [tipo, setTipo] = useState("general");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try { setData(await api(`/admin/constructora/reportes/${tipo}?proyecto_id=${proyecto.id}`)); } catch (e) { toast.error(e.message); setData(null); }
    setLoading(false);
  }, [api, tipo, proyecto.id]);
  useEffect(() => { cargar(); }, [cargar]);

  const rep = data ? buildReporteConstructora(tipo, data) : null;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {[["general", "General (cobros / costos / margen)"], ["costos", "Costos por grupo"], ["cronograma", "Cronograma y atrasos"], ["equipos", "Equipos"]].map(([k, l]) => (
          <button key={k} onClick={() => setTipo(k)} className={`px-3 py-1.5 rounded-lg text-sm border ${tipo === k ? "bg-arandu-blue border-arandu-blue text-white" : "border-white/10 text-slate-300 bg-white/5"}`}>{l}</button>
        ))}
        <button className={`${BTN_SEC} ml-auto`} onClick={cargar}><RefreshCw className="w-4 h-4" /></button>
        {rep && <button className={BTN_PRI} onClick={() => imprimirReporte(rep)}><Printer className="w-4 h-4" /> Imprimir</button>}
      </div>
      {loading && <Loader2 className="w-6 h-6 animate-spin text-arandu-blue" />}
      {rep && !loading && (
        <div className="space-y-4">
          <div className={`${CARD} p-4 grid gap-x-8 gap-y-1 sm:grid-cols-2 lg:grid-cols-3 text-sm`}>
            {rep.summary.map(([l, v]) => <div key={l} className="flex justify-between gap-3 border-b border-white/5 py-1"><span className="text-slate-400">{l}</span><span className="text-white font-medium">{v}</span></div>)}
          </div>
          {rep.sections.map((s) => (
            <div key={s.title} className={`${CARD} overflow-x-auto`}>
              <div className="px-3 py-2 text-sm font-semibold text-white border-b border-white/10">{s.title}</div>
              <table className="w-full text-xs">
                <thead><tr className="text-slate-400 text-left">{s.columns.map((c) => <th key={c} className="px-3 py-1.5">{c}</th>)}</tr></thead>
                <tbody>
                  {s.rows.length === 0 && <tr><td colSpan={s.columns.length} className="px-3 py-4 text-center text-slate-500">Sin datos</td></tr>}
                  {s.rows.map((r, i) => <tr key={i} className="border-t border-white/5 text-slate-200">{r.map((c, j) => <td key={j} className="px-3 py-1.5">{c}</td>)}</tr>)}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
